import React, { useEffect, useMemo, useState } from "react";
import Page from "../../components/Page";
import {
  Refrigerator,
  Snowflake,
  Flame,
  Waves,
  Wind,
  CookingPot,
  ChefHat,
  Trash2,
  LayoutGrid,
  Forklift,
  ScanLine,
  Soup,
  ShieldAlert,
  ClipboardPen,
  Camera,
  Image,
  Upload,
  Hammer,
  Ban,
  UserX,
  Store,
  Sparkles,
} from "lucide-react";

const SANIFICATION_PRODUCTS = [
  {
    label: "Sgrassatore alcalino per acciaio",
    when: "Giornaliero",
    contact: "5-10 min",
    ppe: "Guanti, occhiali",
    notes: "Risciacquare con acqua potabile dopo l’uso.",
  },
  {
    label: "Detergente neutro per superfici",
    when: "Al bisogno",
    contact: "—",
    ppe: "Guanti",
    notes: "Adatto a superfici lavabili e piani non delicati.",
  },
  {
    label: "Disinfettante clorossidante",
    when: "Fine turno / Settimanale",
    contact: "10 min",
    ppe: "Guanti, mascherina",
    notes: "Non usare su alluminio. Risciacquare accuratamente.",
  },
  {
    label: "Detergente acido anticalcare",
    when: "Settimanale",
    contact: "5 min",
    ppe: "Guanti",
    notes: "Per lavelli e accumuli di calcare. Non miscelare con cloro.",
  },
];

const JUSTIFICATION_OPTIONS = [
  "Area in manutenzione",
  "Area inaccessibile",
  "Operatore non in sede",
  "Chiusura ristorante",
  "Altro",
];

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Giornaliera" },
  { value: "weekly", label: "Settimanale" },
  { value: "biweekly", label: "Bisettimanale" },
  { value: "monthly", label: "Mensile" },
];

function normalizeFrequency(v) {
  const x = String(v || "").trim().toLowerCase();

  if (["giornaliera", "giornaliero", "daily"].includes(x)) return "daily";
  if (["settimanale", "weekly"].includes(x)) return "weekly";
  if (["bisettimanale", "quindicinale", "biweekly"].includes(x)) return "biweekly";
  if (["mensile", "monthly"].includes(x)) return "monthly";

  return "daily";
}

function frequencyLabel(v) {
  return FREQUENCY_OPTIONS.find((x) => x.value === v)?.label || "Giornaliera";
}

function frequencyDays(v) {
  switch (normalizeFrequency(v)) {
    case "daily":
      return 1;
    case "weekly":
      return 7;
    case "biweekly":
      return 14;
    case "monthly":
      return 30;
    default:
      return 1;
  }
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function diffDays(fromDate, toDate) {
  const ms = toDate.getTime() - fromDate.getTime();
  return Math.ceil(ms / 86400000);
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT");
  } catch {
    return iso;
  }
}

function formatDateShort(date) {
  try {
    return new Date(date).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function getDefaultFrequencyForItem(item) {
  const name = String(item?.nome || "").toLowerCase();
  const category = String(item?.categoria || "").toLowerCase();
  const text = `${name} ${category}`;

  if (text.includes("freezer")) return "monthly";
  if (text.includes("frigorif")) return "weekly";
  if (text.includes("abbattit")) return "daily";
  if (text.includes("cappa")) return "monthly";
  if (text.includes("piano di cottura")) return "daily";
  if (text.includes("friggitric")) return "weekly";
  if (text.includes("forn")) return "weekly";
  if (text.includes("lavabi")) return "daily";
  if (text.includes("lavastoviglie")) return "daily";
  if (text.includes("scaffal")) return "weekly";
  if (text.includes("armadi di cucina")) return "weekly";
  if (text.includes("contenitori per i rifiuti")) return "daily";
  if (text.includes("pareti tinteggiate")) return "biweekly";
  if (text.includes("pareti piastrellate")) return "weekly";
  if (text.includes("paviment")) return "daily";
  if (text.includes("toilet")) return "daily";
  if (text.includes("spogliatoi")) return "weekly";
  if (text.includes("distributori di bibite")) return "daily";
  if (text.includes("piccole attrezzature")) return "daily";
  if (text.includes("tritacarne")) return "daily";
  if (text.includes("affettatric")) return "daily";
  if (text.includes("pelapatate")) return "daily";
  if (text.includes("tritaverdure")) return "daily";
  if (text.includes("utensili")) return "daily";
  if (text.includes("taglieri")) return "daily";
  if (text.includes("piani di lavoro")) return "daily";
  if (text.includes("taglieri di teflon")) return "daily";

  return "daily";
}

function computeScheduleInfo(state, frequency) {
  const freq = normalizeFrequency(frequency);
  const interval = frequencyDays(freq);
  const doneAt = state?.status === "done" && state?.updatedAt ? new Date(state.updatedAt) : null;

  if (!doneAt || Number.isNaN(doneAt.getTime())) {
    return {
      isDue: true,
      effectiveStatus:
        state?.status === "later"
          ? "later"
          : state?.status === "justified"
            ? "justified"
            : "todo",
      nextDueAt: startOfToday(),
      daysLeft: 0,
      intervalDays: interval,
    };
  }

  const nextDueAt = addDays(doneAt, interval);
  const today = startOfToday();
  const dueToday = startOfToday();
  const nextDueStart = new Date(nextDueAt);
  nextDueStart.setHours(0, 0, 0, 0);

  const isDue = nextDueStart <= dueToday;
  const daysLeft = Math.max(0, diffDays(today, nextDueStart));

  return {
    isDue,
    effectiveStatus: isDue
      ? state?.status === "later"
        ? "later"
        : state?.status === "justified"
          ? "justified"
          : "todo"
      : "done",
    nextDueAt,
    daysLeft,
    intervalDays: interval,
  };
}

function getSuggestedProductForItem(item) {
  const text = `${item?.nome || ""} ${item?.categoria || ""}`.toLowerCase();

  if (
    text.includes("lavello") ||
    text.includes("lavabi") ||
    text.includes("calcare") ||
    text.includes("rubinet")
  ) {
    return SANIFICATION_PRODUCTS[3];
  }

  if (
    text.includes("piano") ||
    text.includes("tagliere") ||
    text.includes("utensil") ||
    text.includes("affettatric") ||
    text.includes("tritacarne") ||
    text.includes("pelapatate") ||
    text.includes("tritaverdure")
  ) {
    return SANIFICATION_PRODUCTS[1];
  }

  if (
    text.includes("forn") ||
    text.includes("cappa") ||
    text.includes("friggitric") ||
    text.includes("cottura") ||
    text.includes("acciaio")
  ) {
    return SANIFICATION_PRODUCTS[0];
  }

  return SANIFICATION_PRODUCTS[2];
}

function getEquipmentVisual(item) {
  const text = `${item?.nome || ""} ${item?.categoria || ""}`.toLowerCase();

  if (text.includes("frigorif")) {
    return { Icon: Refrigerator, color: "text-sky-600", bg: "bg-sky-50" };
  }
  if (text.includes("freezer")) {
    return { Icon: Snowflake, color: "text-cyan-600", bg: "bg-cyan-50" };
  }
  if (text.includes("forn")) {
    return { Icon: Flame, color: "text-orange-600", bg: "bg-orange-50" };
  }
  if (text.includes("lavello") || text.includes("lavabi")) {
    return { Icon: Waves, color: "text-blue-600", bg: "bg-blue-50" };
  }
  if (text.includes("cappa")) {
    return { Icon: Wind, color: "text-slate-600", bg: "bg-slate-50" };
  }
  if (text.includes("friggitric")) {
    return { Icon: CookingPot, color: "text-amber-600", bg: "bg-amber-50" };
  }
  if (text.includes("piano di cottura")) {
    return { Icon: ChefHat, color: "text-rose-600", bg: "bg-rose-50" };
  }
  if (text.includes("rifiuti")) {
    return { Icon: Trash2, color: "text-red-600", bg: "bg-red-50" };
  }
  if (text.includes("scaffal") || text.includes("armadi")) {
    return { Icon: LayoutGrid, color: "text-indigo-600", bg: "bg-indigo-50" };
  }
  if (text.includes("abbattit")) {
    return { Icon: Snowflake, color: "text-teal-600", bg: "bg-teal-50" };
  }
  if (text.includes("lavastoviglie")) {
    return { Icon: Sparkles, color: "text-violet-600", bg: "bg-violet-50" };
  }
  if (text.includes("distributori")) {
    return { Icon: Soup, color: "text-emerald-600", bg: "bg-emerald-50" };
  }
  if (text.includes("pareti") || text.includes("paviment")) {
    return { Icon: Forklift, color: "text-stone-600", bg: "bg-stone-50" };
  }
  if (text.includes("piccole attrezzature")) {
    return { Icon: ScanLine, color: "text-fuchsia-600", bg: "bg-fuchsia-50" };
  }

  return { Icon: Sparkles, color: "text-emerald-600", bg: "bg-emerald-50" };
}

function StatusBadge({ status }) {
  const map = {
    todo: {
      label: "Da fare",
      cls: "bg-orange-100 text-orange-700 border-orange-200",
      dot: "bg-orange-400",
    },
    done: {
      label: "Completato",
      cls: "bg-green-100 text-green-700 border-green-200",
      dot: "bg-green-500",
    },
    later: {
      label: "Posticipato",
      cls: "bg-yellow-100 text-yellow-700 border-yellow-200",
      dot: "bg-yellow-400",
    },
    justified: {
      label: "Giustificato",
      cls: "bg-slate-100 text-slate-700 border-slate-200",
      dot: "bg-slate-500",
    },
  };
  const s = map[status] || map.todo;
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${s.cls}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function ProgressRing({ pct }) {
  const r = 40;
  const c = 2 * Math.PI * r;
  const dashOffset = c - (pct / 100) * c;

  return (
    <div className="hidden md:flex items-center justify-center relative w-24 h-24 shrink-0">
      <svg className="transform -rotate-90 w-24 h-24">
        <circle
          cx="48"
          cy="48"
          r={r}
          stroke="currentColor"
          strokeWidth="8"
          fill="transparent"
          className="text-emerald-600/10"
        />
        <circle
          cx="48"
          cy="48"
          r={r}
          stroke="#2AC99F"
          strokeWidth="8"
          fill="transparent"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-xl font-bold text-emerald-700">{pct}%</span>
    </div>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[999] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-2xl rounded-3xl bg-white border border-gray-200 shadow-2xl p-5 md:p-6 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button
            className="h-10 w-10 rounded-2xl border border-gray-200 hover:bg-gray-50 text-gray-500 transition"
            onClick={onClose}
            aria-label="Chiudi"
            type="button"
          >
            ✕
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function InfoTable({ product }) {
  const rows = [
    ["Frequenza consigliata", product?.when || "—"],
    ["Tempo di contatto", product?.contact || "—"],
    ["DPI consigliati", product?.ppe || "—"],
    ["Note prodotto", product?.notes || "—"],
  ];

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      <table className="w-full border-collapse">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b border-gray-200 last:border-b-0">
              <th className="text-left w-[42%] bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700">
                {k}
              </th>
              <td className="px-4 py-3 text-sm text-gray-800">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Sanificazione() {
  const [catalog, setCatalog] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMeta, setSelectedMeta] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [savingPick, setSavingPick] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState({});
  const [uploadingReferencePhoto, setUploadingReferencePhoto] = useState({});
  const [err, setErr] = useState("");

  const [pickOpen, setPickOpen] = useState(false);
  const [pickDraft, setPickDraft] = useState([]);
  const [pickDraftMeta, setPickDraftMeta] = useState({});
  const [pickQuery, setPickQuery] = useState("");
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestName, setRequestName] = useState("");
  const [requestCategory, setRequestCategory] = useState("");
  const [requestNote, setRequestNote] = useState("");
  const [requestSaving, setRequestSaving] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState("");

  // { [id]: { status, updatedAt, justificationReason, justificationNote, productName, usageInstructions, safetyNotes, imageUrl } }
  const [states, setStates] = useState({});

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [selectedProductIdx, setSelectedProductIdx] = useState(0);
  const [instructionForm, setInstructionForm] = useState({
    productName: "",
    usageInstructions: "",
    safetyNotes: "",
    imageUrl: "",
  });
  const [savingInstructions, setSavingInstructions] = useState(false);

  const [justifyOpen, setJustifyOpen] = useState(false);
  const [justifyItem, setJustifyItem] = useState(null);
  const [justifyReason, setJustifyReason] = useState(JUSTIFICATION_OPTIONS[0]);
  const [justifyNote, setJustifyNote] = useState("");

  async function saveStatuses(items, options = {}) {
    const { silent = false } = options;

    try {
      if (!silent) setSavingStatus(true);

      const res = await fetch("/api/sanificazione_progress_save.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Errore salvataggio avanzamento");
      }

      return true;
    } catch (e) {
      setErr(e?.message || "Errore salvataggio avanzamento");
      return false;
    } finally {
      if (!silent) setSavingStatus(false);
    }
  }

  async function resetStatusesOnServer() {
    try {
      setSavingStatus(true);

      const res = await fetch("/api/sanificazione_progress_save.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reset_all: true }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Errore reset avanzamento");
      }

      return true;
    } catch (e) {
      setErr(e?.message || "Errore reset avanzamento");
      return false;
    } finally {
      setSavingStatus(false);
    }
  }

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingCatalog(true);
        setErr("");

        const [cRes, sRes, pRes] = await Promise.all([
          fetch("/api/attrezzature_list.php", { credentials: "include" }),
          fetch("/api/attrezzature_selected_get.php", { credentials: "include" }),
          fetch("/api/sanificazione_progress_get.php", { credentials: "include" }),
        ]);

        const cJson = await cRes.json().catch(() => null);
        const sJson = await sRes.json().catch(() => null);
        const pJson = await pRes.json().catch(() => null);

        if (!alive) return;

        if (!cRes.ok || !cJson?.success) {
          throw new Error(cJson?.error || "Errore caricamento catalogo attrezzature");
        }
        if (!sRes.ok || !sJson?.success) {
          throw new Error(sJson?.error || "Errore caricamento selezione attrezzature");
        }
        if (!pRes.ok || !pJson?.success) {
          throw new Error(pJson?.error || "Errore caricamento avanzamento sanificazione");
        }

        const cat = Array.isArray(cJson.items) ? cJson.items : [];
        setCatalog(cat);

        let selIds = [];
        let meta = {};

        if (Array.isArray(sJson.items)) {
          selIds = sJson.items
            .map((x) => Number(x.attrezzatura_id || x.id))
            .filter(Boolean);

          sJson.items.forEach((x) => {
            const id = Number(x.attrezzatura_id || x.id);
            if (!id) return;
            meta[id] = {
              frequency: normalizeFrequency(x.frequency || x.frequenza || "daily"),
              referencePhotoUrl: x.referencePhotoUrl || x.reference_photo_url || "",
            };
          });
        } else {
          selIds = Array.isArray(sJson.selectedIds) ? sJson.selectedIds.map(Number).filter(Boolean) : [];
        }

        for (const id of selIds) {
          if (!meta[id]) {
            const item = cat.find((x) => Number(x.id) === Number(id));
            meta[id] = {
              frequency: getDefaultFrequencyForItem(item),
              referencePhotoUrl: "",
            };
          }
        }

        setSelectedIds(selIds);
        setSelectedMeta(meta);

        const progress = pJson?.items && typeof pJson.items === "object" ? pJson.items : {};
        const initialStates = {};
        selIds.forEach((id) => {
          const row = progress[id] || progress[String(id)] || null;
          initialStates[id] = {
            status: row?.status || "todo",
            updatedAt: row?.updatedAt || null,
            justificationReason: row?.justificationReason || row?.justification_reason || "",
            justificationNote: row?.justificationNote || row?.justification_note || "",
            productName: row?.productName || row?.product_name || "",
            usageInstructions: row?.usageInstructions || row?.usage_instructions || "",
            safetyNotes: row?.safetyNotes || row?.safety_notes || "",
            imageUrl: row?.imageUrl || row?.image_url || row?.foto_url || "",
            proofPhotoUrl: row?.proofPhotoUrl || row?.proof_photo_url || "",
            proofPhotoUploadedAt: row?.proofPhotoUploadedAt || row?.proof_photo_uploaded_at || "",
          };
        });
        setStates(initialStates);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Errore caricamento dati");
      } finally {
        if (!alive) return;
        setLoadingCatalog(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setStates((prev) => {
      const next = { ...prev };

      for (const id of selectedIds) {
        if (!next[id]) {
          next[id] = {
            status: "todo",
            updatedAt: null,
            justificationReason: "",
            justificationNote: "",
            productName: "",
            usageInstructions: "",
            safetyNotes: "",
            imageUrl: "",
            proofPhotoUrl: "",
            proofPhotoUploadedAt: "",
          };
        }
      }

      Object.keys(next).forEach((k) => {
        if (!selectedIds.includes(Number(k))) delete next[k];
      });

      return next;
    });

    setSelectedMeta((prev) => {
      const next = { ...prev };
      for (const id of selectedIds) {
        if (!next[id]) {
          const item = catalog.find((x) => Number(x.id) === Number(id));
          next[id] = { frequency: getDefaultFrequencyForItem(item), referencePhotoUrl: "" };
        }
      }
      Object.keys(next).forEach((k) => {
        if (!selectedIds.includes(Number(k))) delete next[k];
      });
      return next;
    });
  }, [selectedIds, catalog]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const enrichedSelectedItems = useMemo(() => {
    return catalog
      .filter((it) => selectedSet.has(it.id))
      .map((it) => {
        const state = states[it.id] || {
          status: "todo",
          updatedAt: null,
          justificationReason: "",
          justificationNote: "",
          productName: "",
          usageInstructions: "",
          safetyNotes: "",
          imageUrl: "",
          proofPhotoUrl: "",
          proofPhotoUploadedAt: "",
        };
        const frequency = selectedMeta[it.id]?.frequency || getDefaultFrequencyForItem(it);
        const schedule = computeScheduleInfo(state, frequency);

        return {
          ...it,
          frequency,
          state,
          schedule,
        };
      });
  }, [catalog, selectedSet, states, selectedMeta]);

  const visibleItems = useMemo(() => {
    const q = (query || "").trim().toLowerCase();

    return enrichedSelectedItems.filter((it) => {
      const matchQuery =
        !q ||
        (it.nome || "").toLowerCase().includes(q) ||
        (it.categoria || "").toLowerCase().includes(q);
      const effectiveStatus = it.schedule.effectiveStatus;
      const matchFilter = filter === "all" ? true : effectiveStatus === filter;
      return matchQuery && matchFilter;
    });
  }, [enrichedSelectedItems, query, filter]);

  async function updateStatus(id, status, extra = {}) {
    const now = new Date().toISOString();
    const prev = states[id] || {
      status: "todo",
      updatedAt: null,
      justificationReason: "",
      justificationNote: "",
      productName: "",
      usageInstructions: "",
      safetyNotes: "",
      imageUrl: "",
      proofPhotoUrl: "",
      proofPhotoUploadedAt: "",
    };

    const nextState = {
      ...prev,
      ...extra,
      status,
      updatedAt: now,
      ...(status === "done" ? {} : { proofPhotoUrl: "", proofPhotoUploadedAt: "" }),
    };

    setStates((current) => ({
      ...current,
      [id]: nextState,
    }));

    const ok = await saveStatuses(
      [
        {
          attrezzatura_id: id,
          status,
          justification_reason: nextState.justificationReason || "",
          justification_note: nextState.justificationNote || "",
          product_name: nextState.productName || "",
          usage_instructions: nextState.usageInstructions || "",
          safety_notes: nextState.safetyNotes || "",
          image_url: nextState.imageUrl || "",
        },
      ],
      { silent: false }
    );

    if (!ok) {
      setStates((current) => ({
        ...current,
        [id]: prev,
      }));
    }
  }

  async function bulkMark(status) {
    if (selectedIds.length === 0) return;

    const dueIds = enrichedSelectedItems
      .filter((x) => x.schedule.isDue && x.schedule.effectiveStatus !== "justified")
      .map((x) => x.id);

    if (dueIds.length === 0) return;

    const now = new Date().toISOString();
    const prev = { ...states };

    setStates((current) => {
      const next = { ...current };
      for (const id of dueIds) {
        next[id] = {
          ...next[id],
          status,
          updatedAt: status === "done" ? now : next[id]?.updatedAt || now,
        };
      }
      return next;
    });

    const items = dueIds.map((id) => ({
      attrezzatura_id: id,
      status,
      justification_reason: states[id]?.justificationReason || "",
      justification_note: states[id]?.justificationNote || "",
      product_name: states[id]?.productName || "",
      usage_instructions: states[id]?.usageInstructions || "",
      safety_notes: states[id]?.safetyNotes || "",
      image_url: states[id]?.imageUrl || "",
    }));

    const ok = await saveStatuses(items, { silent: false });

    if (!ok) {
      setStates(prev);
    }
  }

  async function resetAll() {
    const prev = { ...states };

    setStates((current) => {
      const next = { ...current };
      for (const id of selectedIds) {
        next[id] = {
          ...next[id],
          status: "todo",
          updatedAt: null,
          justificationReason: "",
          justificationNote: "",
        };
      }
      return next;
    });

    const ok = await resetStatusesOnServer();

    if (!ok) {
      setStates(prev);
    }
  }

  async function uploadProofPhoto(id, file) {
    if (!file) return;

    const prev = states[id] || {};
    const form = new FormData();
    form.append("attrezzatura_id", String(id));
    form.append("photo", file);

    try {
      setUploadingPhoto((current) => ({ ...current, [id]: true }));
      setErr("");

      const res = await fetch("/api/sanificazione_photo_upload.php", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Errore caricamento foto");
      }

      setStates((current) => ({
        ...current,
        [id]: {
          ...(current[id] || {}),
          proofPhotoUrl: json.url || "",
          proofPhotoUploadedAt: json.uploadedAt || new Date().toISOString(),
        },
      }));
    } catch (e) {
      setErr(e?.message || "Errore caricamento foto");
      setStates((current) => ({
        ...current,
        [id]: prev,
      }));
    } finally {
      setUploadingPhoto((current) => ({ ...current, [id]: false }));
    }
  }

  async function uploadReferencePhoto(id, file) {
    if (!file) return;

    const prevMeta = selectedMeta[id] || {};
    const form = new FormData();
    form.append("attrezzatura_id", String(id));
    form.append("photo", file);

    try {
      setUploadingReferencePhoto((current) => ({ ...current, [id]: true }));
      setErr("");

      const res = await fetch("/api/sanificazione_reference_photo_upload.php", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Errore caricamento foto riferimento");
      }

      setSelectedMeta((current) => ({
        ...current,
        [id]: {
          ...(current[id] || {}),
          referencePhotoUrl: json.url || "",
        },
      }));
    } catch (e) {
      setErr(e?.message || "Errore caricamento foto riferimento");
      setSelectedMeta((current) => ({
        ...current,
        [id]: prevMeta,
      }));
    } finally {
      setUploadingReferencePhoto((current) => ({ ...current, [id]: false }));
    }
  }

  function openInstructions(item) {
    const currentState = states[item.id] || {};
    const suggested = getSuggestedProductForItem(item);
    const initialIdx = Math.max(
      0,
      SANIFICATION_PRODUCTS.findIndex((p) => p.label === suggested.label)
    );

    setModalItem(item);
    setSelectedProductIdx(initialIdx);
    setInstructionForm({
      productName: currentState.productName || suggested.label,
      usageInstructions:
        currentState.usageInstructions ||
        `Applicare il prodotto sulla superficie di ${item.nome}, rispettare il tempo di contatto indicato, quindi risciacquare se previsto.`,
      safetyNotes:
        currentState.safetyNotes || suggested.notes || "Usare i DPI previsti e seguire l’etichetta del prodotto.",
      imageUrl: currentState.imageUrl || item?.foto_url || item?.image_url || "",
    });
    setModalOpen(true);
  }

  function openPickModal() {
    const meta = {};
    selectedIds.forEach((id) => {
      meta[id] = {
        frequency:
          selectedMeta[id]?.frequency ||
          getDefaultFrequencyForItem(catalog.find((x) => Number(x.id) === Number(id))),
        referencePhotoUrl: selectedMeta[id]?.referencePhotoUrl || "",
      };
    });

    setPickDraft(selectedIds);
    setPickDraftMeta(meta);
    setPickQuery("");
    setErr("");
    setPickOpen(true);
  }

  function openRequestModal() {
    setRequestSuccess("");
    setRequestName("");
    setRequestCategory("");
    setRequestNote("");
    setErr("");
    setRequestOpen(true);
  }

  function openJustification(item) {
    const currentState = states[item.id] || {};
    setJustifyItem(item);
    setJustifyReason(currentState.justificationReason || JUSTIFICATION_OPTIONS[0]);
    setJustifyNote(currentState.justificationNote || "");
    setJustifyOpen(true);
  }

  function setDraftFrequency(id, frequency) {
    setPickDraftMeta((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        frequency: normalizeFrequency(frequency),
      },
    }));
  }

  function restoreDefaultFrequencies() {
    const next = { ...pickDraftMeta };
    for (const id of pickDraft) {
      const item = catalog.find((x) => Number(x.id) === Number(id));
      next[id] = {
        frequency: getDefaultFrequencyForItem(item),
        referencePhotoUrl: pickDraftMeta[id]?.referencePhotoUrl || selectedMeta[id]?.referencePhotoUrl || "",
      };
    }
    setPickDraftMeta(next);
  }

  async function savePickSelection() {
    try {
      setSavingPick(true);
      setErr("");

      const items = pickDraft.map((id) => {
        const item = catalog.find((x) => Number(x.id) === Number(id));
        return {
          attrezzatura_id: id,
          frequency: normalizeFrequency(
            pickDraftMeta[id]?.frequency || getDefaultFrequencyForItem(item)
          ),
          referencePhotoUrl: pickDraftMeta[id]?.referencePhotoUrl || selectedMeta[id]?.referencePhotoUrl || "",
        };
      });

      const res = await fetch("/api/attrezzature_selected_save.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selectedIds: pickDraft,
          items,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Errore salvataggio selezione");
      }

      setSelectedIds(pickDraft);

      const nextMeta = {};
      pickDraft.forEach((id) => {
        const item = catalog.find((x) => Number(x.id) === Number(id));
        nextMeta[id] = {
          frequency: normalizeFrequency(
            pickDraftMeta[id]?.frequency || getDefaultFrequencyForItem(item)
          ),
          referencePhotoUrl: pickDraftMeta[id]?.referencePhotoUrl || selectedMeta[id]?.referencePhotoUrl || "",
        };
      });
      setSelectedMeta(nextMeta);

      setStates((prev) => {
        const next = {};
        pickDraft.forEach((id) => {
          next[id] = prev[id] || {
            status: "todo",
            updatedAt: null,
            justificationReason: "",
            justificationNote: "",
            productName: "",
            usageInstructions: "",
            safetyNotes: "",
            imageUrl: "",
            proofPhotoUrl: "",
            proofPhotoUploadedAt: "",
          };
        });
        return next;
      });

      setPickOpen(false);
    } catch (e) {
      setErr(e?.message || "Errore salvataggio");
    } finally {
      setSavingPick(false);
    }
  }

  const pickFiltered = useMemo(() => {
    const q = (pickQuery || "").trim().toLowerCase();
    return catalog.filter(
      (it) =>
        !q ||
        (it.nome || "").toLowerCase().includes(q) ||
        (it.categoria || "").toLowerCase().includes(q)
    );
  }, [catalog, pickQuery]);

  async function submitNewEquipmentRequest() {
    try {
      setRequestSaving(true);
      setErr("");
      setRequestSuccess("");

      const res = await fetch("/api/attrezzature_proposta_create.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: requestName,
          categoria: requestCategory,
          note: requestNote,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Errore invio richiesta");
      }

      setRequestSuccess(
        json?.message || "Richiesta inviata correttamente. Verrà valutata dal superadmin."
      );
      setRequestName("");
      setRequestCategory("");
      setRequestNote("");
    } catch (e) {
      setErr(e?.message || "Errore invio richiesta");
    } finally {
      setRequestSaving(false);
    }
  }

  async function saveInstructions() {
    if (!modalItem) return;

    try {
      setSavingInstructions(true);
      setErr("");

      const prev = states[modalItem.id] || {};
      const nextState = {
        ...prev,
        productName: instructionForm.productName,
        usageInstructions: instructionForm.usageInstructions,
        safetyNotes: instructionForm.safetyNotes,
        imageUrl: instructionForm.imageUrl,
      };

      setStates((current) => ({
        ...current,
        [modalItem.id]: {
          ...current[modalItem.id],
          ...nextState,
        },
      }));

      const ok = await saveStatuses(
        [
          {
            attrezzatura_id: modalItem.id,
            status: prev.status || "todo",
            justification_reason: prev.justificationReason || "",
            justification_note: prev.justificationNote || "",
            product_name: instructionForm.productName || "",
            usage_instructions: instructionForm.usageInstructions || "",
            safety_notes: instructionForm.safetyNotes || "",
            image_url: instructionForm.imageUrl || "",
          },
        ],
        { silent: true }
      );

      if (!ok) {
        setStates((current) => ({
          ...current,
          [modalItem.id]: prev,
        }));
        return;
      }

      setModalOpen(false);
    } catch (e) {
      setErr(e?.message || "Errore salvataggio istruzioni");
    } finally {
      setSavingInstructions(false);
    }
  }

  async function saveJustification() {
    if (!justifyItem) return;

    await updateStatus(justifyItem.id, "justified", {
      justificationReason: justifyReason,
      justificationNote: justifyNote,
    });

    setJustifyOpen(false);
  }

  const counts = useMemo(() => {
    const dueItems = enrichedSelectedItems.filter((it) => it.schedule.isDue);
    const actionable = dueItems.filter((it) => it.schedule.effectiveStatus !== "justified");
    const total = actionable.length;
    const done = actionable.filter((it) => it.schedule.effectiveStatus === "done").length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 100;

    return {
      total,
      done,
      pct,
      overdue: actionable.filter((it) => it.schedule.effectiveStatus !== "done").length,
      justified: dueItems.filter((it) => it.schedule.effectiveStatus === "justified").length,
      selectedTotal: selectedIds.length,
    };
  }, [enrichedSelectedItems, selectedIds.length]);

  return (
    <Page title="Sanificazione" back>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6">
        <div className="rounded-3xl p-6 md:p-8 shadow-sm relative overflow-hidden bg-emerald-50 border-2 border-emerald-600 text-emerald-700">
          <div className="relative z-10 flex flex-col md:flex-row gap-6 justify-between items-end md:items-center">
            <div className="w-full md:w-2/3">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border border-emerald-600 bg-emerald-600/5 text-emerald-700">
                  Piano per scadenze
                </span>
                <span className="text-emerald-700/80 text-sm font-mono">
                  {new Date().toLocaleDateString("it-IT", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-bold mb-4 text-emerald-700">
                Hai completato {counts.done} attività in scadenza su {counts.total}
              </h1>

              <div className="w-full bg-white border border-emerald-600/20 rounded-full h-3 mb-2 overflow-hidden">
                <div
                  className="bg-[#2AC99F] h-3 rounded-full shadow-[0_0_10px_rgba(42,201,159,0.3)] transition-all"
                  style={{ width: `${counts.pct}%` }}
                />
              </div>

              <p className="text-emerald-700/80 text-sm">
                {counts.selectedTotal === 0
                  ? "Seleziona prima l’attrezzatura disponibile."
                  : counts.total === 0 && counts.justified === 0
                    ? "Nessuna attività in scadenza oggi. Ottimo."
                    : counts.pct === 100 && counts.total > 0
                      ? "Ottimo! Tutte le attività in scadenza sono state completate."
                      : `Restano ${counts.overdue} attività in scadenza da completare${counts.justified > 0 ? ` e ${counts.justified} giustificate` : ""
                      }.`}
              </p>
            </div>

            <ProgressRing pct={counts.pct} />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 border-b border-gray-200/60 pb-3">
          <div>
            <h2 className="text-3xl text-gray-800 font-light tracking-tight">
              Piano di Sanificazione
            </h2>
            <p className="text-gray-400 text-sm mt-1 font-light">
              Seleziona l’attrezzatura reale, imposta la frequenza e registra solo ciò che è davvero in scadenza.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 w-full lg:w-auto">
            <button
              className="h-11 px-5 rounded-2xl bg-[#003B71] text-white font-bold shadow-sm hover:shadow-md transition"
              onClick={openPickModal}
              type="button"
            >
              Scegli attrezzatura disponibile
            </button>

            <button
              className="h-11 px-5 rounded-2xl bg-white border border-[#003B71]/20 text-[#003B71] font-bold hover:bg-[#003B71]/5 transition"
              onClick={openRequestModal}
              type="button"
            >
              Richiedi nuova attrezzatura
            </button>

            <button
              className="h-11 px-5 rounded-2xl bg-gray-900 text-white font-bold shadow-sm hover:shadow-md transition disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => bulkMark("done")}
              disabled={selectedIds.length === 0 || savingStatus}
              type="button"
            >
              Segna tutte le scadenze come fatte
            </button>

            <button
              className="h-11 px-5 rounded-2xl bg-white border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={resetAll}
              disabled={selectedIds.length === 0 || savingStatus}
              type="button"
            >
              Reset
            </button>
          </div>
        </div>

        {err ? (
          <div className="rounded-3xl border-2 border-red-200 bg-red-50 p-5 text-red-800">
            {err}
          </div>
        ) : null}

        <div className="flex flex-col md:flex-row w-full gap-4 items-stretch md:items-center">
          <div className="relative flex-1 md:w-80">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
            <input
              type="text"
              placeholder="Cerca attrezzatura..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={selectedIds.length === 0}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-[20px] text-sm focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all shadow-sm disabled:opacity-50"
            />
          </div>

          <div className="flex items-center bg-white p-1 rounded-[24px] shadow-sm border border-gray-200 w-full md:w-auto shrink-0 flex-wrap">
            {[
              { key: "todo", label: "Da fare", dot: "bg-orange-400" },
              { key: "done", label: "Completati", dot: "bg-green-500" },
              { key: "later", label: "Posticipati", dot: "bg-yellow-400" },
              { key: "justified", label: "Giustificati", dot: "bg-slate-500" },
              { key: "all", label: "Tutti", dot: "bg-gray-300" },
            ].map((t) => {
              const active = filter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  disabled={selectedIds.length === 0}
                  type="button"
                  className={[
                    "flex-1 px-5 py-2.5 rounded-[20px] text-sm font-bold transition-all flex items-center justify-center gap-2",
                    active ? "bg-[#003B71] text-white shadow-md" : "text-gray-500 hover:bg-gray-50",
                    selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : "",
                  ].join(" ")}
                >
                  <span className={`w-2 h-2 rounded-full ${active ? t.dot : "bg-gray-300"}`} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {loadingCatalog ? (
          <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-gray-300 text-gray-500">
            Caricamento attrezzature…
          </div>
        ) : selectedIds.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-gray-300">
            <div className="text-gray-500">
              Nessuna attrezzatura selezionata per questa azienda.
            </div>
            <div className="text-gray-400 text-sm mt-2">
              Clicca <b>“Scegli attrezzatura disponibile”</b> per selezionare ciò che hai davvero.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {visibleItems.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-gray-300 text-gray-500">
                Nessun elemento trovato.
              </div>
            ) : (
              visibleItems.map((it) => {
                const effectiveStatus = it.schedule.effectiveStatus;
                const isDone = effectiveStatus === "done";
                const isJustified = effectiveStatus === "justified";
                const visual = getEquipmentVisual(it);
                const VisualIcon = visual.Icon;
                const referencePhotoUrl = selectedMeta[it.id]?.referencePhotoUrl || "";
                const imageSrc = referencePhotoUrl || it.state?.imageUrl || it.foto_url || it.image_url || "";
                const proofPhotoUrl = it.state?.proofPhotoUrl || "";
                const isUploadingPhoto = !!uploadingPhoto[it.id];
                const isUploadingReferencePhoto = !!uploadingReferencePhoto[it.id];

                return (
                  <div
                    key={it.id}
                    className={[
                      "rounded-3xl p-5 border transition-all duration-300 flex flex-col gap-4",
                      isDone
                        ? "bg-gray-50 border-gray-100 opacity-80 hover:opacity-100"
                        : isJustified
                          ? "bg-slate-50 border-slate-200 border-2 shadow-sm hover:shadow-md"
                          : effectiveStatus === "later"
                            ? "bg-yellow-50 border-yellow-200 border-2 shadow-sm hover:shadow-md"
                            : "bg-white border-gray-100 hover:border-[#003B71]/20 shadow-sm hover:shadow-md",
                    ].join(" ")}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <label
                        className={`group relative w-14 h-14 rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden flex items-center justify-center shrink-0 cursor-pointer ${visual.bg}`}
                        title="Carica foto riferimento"
                      >
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={it.nome}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <VisualIcon className={`w-7 h-7 ${visual.color}`} />
                        )}
                        <span className="absolute inset-0 bg-black/45 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                          {isUploadingReferencePhoto ? (
                            <Upload className="w-4 h-4 animate-pulse" />
                          ) : (
                            <Camera className="w-4 h-4" />
                          )}
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          disabled={isUploadingReferencePhoto}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            uploadReferencePhoto(it.id, file);
                          }}
                        />
                      </label>

                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className={`text-lg font-bold ${isDone ? "line-through text-gray-400" : "text-gray-800"}`}>
                            {it.nome}
                          </h3>
                          <StatusBadge status={effectiveStatus} />
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-blue-50 text-blue-700 border-blue-200">
                            {frequencyLabel(it.frequency)}
                          </span>
                          {referencePhotoUrl ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">
                              <Camera className="w-3 h-3" />
                              Foto riferimento
                            </span>
                          ) : null}
                        </div>

                        <p className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
                          {it.categoria ? (
                            <>
                              <span className="inline-flex items-center gap-1">📍 {it.categoria}</span>
                              <span className="w-1 h-1 rounded-full bg-gray-300" />
                            </>
                          ) : null}

                          <span>
                            {it.state.updatedAt
                              ? `Ultimo aggiornamento: ${formatDate(it.state.updatedAt)}`
                              : "Mai registrato"}
                          </span>

                          <span className="w-1 h-1 rounded-full bg-gray-300" />

                          <span>
                            {it.schedule.isDue
                              ? "In scadenza ora"
                              : `Prossima scadenza: ${formatDateShort(it.schedule.nextDueAt)}`}
                          </span>
                        </p>

                        {isJustified && it.state?.justificationReason ? (
                          <div className="mt-2 text-xs text-slate-700 bg-slate-100 border border-slate-200 rounded-xl px-3 py-2 inline-flex flex-col gap-1">
                            <span className="font-semibold">
                              Motivo: {it.state.justificationReason}
                            </span>
                            {it.state.justificationNote ? (
                              <span className="text-slate-600">{it.state.justificationNote}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2 md:pt-0 border-t md:border-t-0 border-gray-100/50 flex-wrap">
                      {!isDone ? (
                        <>
                          <button
                            className="flex-1 md:flex-none h-10 px-4 rounded-[14px] bg-white border border-gray-200 text-gray-500 hover:text-[#003B71] hover:border-[#003B71] hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                            onClick={() => openInstructions(it)}
                            type="button"
                            title="Istruzioni"
                          >
                            <ClipboardPen className="w-4 h-4" />
                            <span className="text-sm font-medium hidden md:inline">Istruzioni</span>
                          </button>

                          <button
                            className="flex-1 md:flex-none h-10 px-4 rounded-[14px] bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                            onClick={() => openJustification(it)}
                            type="button"
                            disabled={savingStatus}
                          >
                            <ShieldAlert className="w-4 h-4" />
                            <span className="text-sm font-bold hidden md:inline">Giustifica</span>
                          </button>

                          <button
                            className="flex-1 md:flex-none h-10 px-4 rounded-[14px] bg-white border border-yellow-200 text-yellow-700 hover:bg-yellow-50 transition-all flex items-center justify-center gap-2"
                            onClick={() => updateStatus(it.id, "later")}
                            type="button"
                            disabled={savingStatus}
                          >
                            ⏱ <span className="text-sm font-bold hidden md:inline">Posticipa</span>
                          </button>

                          <button
                            className="flex-[2] md:flex-none h-10 px-6 rounded-[14px] bg-[#2AC99F] hover:bg-[#20A683] text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 font-bold active:scale-95 disabled:opacity-60"
                            onClick={() => updateStatus(it.id, "done")}
                            type="button"
                            disabled={savingStatus}
                          >
                            ✓ <span>FATTO</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="text-gray-400 text-sm hover:text-gray-600 underline decoration-dotted mr-2"
                            onClick={() => openInstructions(it)}
                            type="button"
                          >
                            Vedi dettagli
                          </button>

                          <button
                            className="h-10 px-4 rounded-[14px] bg-white border border-[#003B71]/30 text-[#003B71] hover:bg-blue-50 transition-all flex items-center justify-center gap-2 font-bold disabled:opacity-60"
                            onClick={() => updateStatus(it.id, "todo")}
                            type="button"
                            disabled={savingStatus}
                          >
                            ↩ Segna da fare
                          </button>
                        </>
                      )}
                    </div>
                    </div>

                    {isDone ? (
                      <div className="rounded-2xl border border-emerald-100 bg-white/80 p-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 rounded-xl border border-emerald-100 bg-emerald-50 flex items-center justify-center overflow-hidden shrink-0">
                            {proofPhotoUrl ? (
                              <a href={proofPhotoUrl} target="_blank" rel="noreferrer" className="block w-full h-full">
                                <img
                                  src={proofPhotoUrl}
                                  alt={`Foto sanificazione ${it.nome}`}
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ) : (
                              <Image className="w-5 h-5 text-emerald-600" />
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="text-sm font-bold text-gray-800 flex items-center gap-2">
                              <Camera className="w-4 h-4 text-emerald-600" />
                              Evidenza fotografica
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {proofPhotoUrl
                                ? `Caricata ${it.state?.proofPhotoUploadedAt ? formatDate(it.state.proofPhotoUploadedAt) : ""}`
                                : "Opzionale: aggiungi una foto della sanificazione completata."}
                            </div>
                          </div>
                        </div>

                        <label className={[
                          "h-10 px-4 rounded-[14px] border font-bold transition-all flex items-center justify-center gap-2 cursor-pointer",
                          isUploadingPhoto
                            ? "bg-gray-100 border-gray-200 text-gray-400 cursor-wait"
                            : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50",
                        ].join(" ")}>
                          <Upload className="w-4 h-4" />
                          <span>{proofPhotoUrl ? "Sostituisci foto" : "Carica foto"}</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            disabled={isUploadingPhoto}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              uploadProofPhoto(it.id, file);
                            }}
                          />
                        </label>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        )}

        <Modal open={pickOpen} onClose={() => setPickOpen(false)} title="Attrezzatura disponibile">
          <div className="grid gap-4">
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                placeholder="Cerca…"
                value={pickQuery}
                onChange={(e) => setPickQuery(e.target.value)}
                className="flex-1 min-w-[220px] px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
              <button
                className="h-12 px-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition disabled:opacity-50"
                onClick={() => {
                  const ids = catalog.map((x) => x.id);
                  setPickDraft(ids);

                  const meta = {};
                  ids.forEach((id) => {
                    const item = catalog.find((x) => Number(x.id) === Number(id));
                    meta[id] = {
                      frequency: pickDraftMeta[id]?.frequency || getDefaultFrequencyForItem(item),
                      referencePhotoUrl: pickDraftMeta[id]?.referencePhotoUrl || selectedMeta[id]?.referencePhotoUrl || "",
                    };
                  });
                  setPickDraftMeta((prev) => ({ ...prev, ...meta }));
                }}
                disabled={catalog.length === 0}
                type="button"
              >
                Seleziona tutti
              </button>
              <button
                className="h-12 px-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition"
                onClick={() => setPickDraft([])}
                type="button"
              >
                Deseleziona tutti
              </button>
              <button
                className="h-12 px-4 rounded-2xl bg-blue-50 border border-blue-200 text-blue-700 font-bold hover:bg-blue-100 transition"
                onClick={restoreDefaultFrequencies}
                type="button"
              >
                Ripristina frequenze standard
              </button>
            </div>

            <div className="text-xs text-gray-500">
              Seleziona solo l’attrezzatura realmente presente nell’azienda. Per ogni voce puoi impostare la frequenza di sanificazione.
            </div>

            <div className="flex justify-between items-center gap-3 flex-wrap rounded-2xl border border-dashed border-blue-200 bg-blue-50/70 px-4 py-3">
              <div className="text-xs text-blue-900">
                Non trovi una voce? Puoi inviare una richiesta di nuova attrezzatura: sarà verificata dal superadmin prima di entrare nel catalogo.
              </div>
              <button
                className="h-10 px-4 rounded-2xl bg-white border border-blue-200 text-blue-700 font-bold hover:bg-blue-100 transition"
                onClick={openRequestModal}
                type="button"
              >
                Invia richiesta
              </button>
            </div>

            {loadingCatalog ? (
              <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-300 text-gray-500">
                Caricamento…
              </div>
            ) : (
              <div className="border border-gray-200 rounded-2xl p-3 max-h-[480px] overflow-auto">
                {pickFiltered.length === 0 ? (
                  <div className="p-3 text-gray-500">Nessun risultato.</div>
                ) : (
                  pickFiltered.map((it) => {
                    const checked = pickDraft.includes(it.id);
                    const freq =
                      pickDraftMeta[it.id]?.frequency || getDefaultFrequencyForItem(it);
                    const visual = getEquipmentVisual(it);
                    const VisualIcon = visual.Icon;

                    return (
                      <div
                        key={it.id}
                        className="flex flex-col md:flex-row gap-3 px-2 py-3 items-start md:items-center border-b border-gray-100 last:border-b-0"
                      >
                        <label className="flex gap-3 items-center cursor-pointer flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setPickDraft((prev) =>
                                prev.includes(it.id)
                                  ? prev.filter((x) => x !== it.id)
                                  : [...prev, it.id]
                              );

                              setPickDraftMeta((prev) => ({
                                ...prev,
                                [it.id]: {
                                  frequency:
                                    prev[it.id]?.frequency || getDefaultFrequencyForItem(it),
                                },
                              }));
                            }}
                          />
                          <div className={`w-10 h-10 rounded-xl ${visual.bg} flex items-center justify-center shrink-0`}>
                            <VisualIcon className={`w-5 h-5 ${visual.color}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-800">{it.nome}</div>
                            {it.categoria ? (
                              <div className="text-xs text-gray-400">{it.categoria}</div>
                            ) : null}
                          </div>
                        </label>

                        <div className="w-full md:w-[220px]">
                          <select
                            value={freq}
                            disabled={!checked}
                            onChange={(e) => setDraftFrequency(it.id, e.target.value)}
                            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71] disabled:opacity-50"
                          >
                            {FREQUENCY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                className="h-11 px-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition disabled:opacity-50"
                onClick={() => setPickOpen(false)}
                disabled={savingPick}
                type="button"
              >
                Annulla
              </button>
              <button
                className="h-11 px-4 rounded-2xl bg-gray-900 text-white font-bold hover:shadow-md transition disabled:opacity-50"
                onClick={savePickSelection}
                disabled={savingPick}
                type="button"
              >
                {savingPick ? "Salvataggio…" : "Salva selezione"}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          title="Richiedi nuova attrezzatura"
        >
          <div className="grid gap-4">
            <div className="text-sm text-gray-600">
              La richiesta verrà inviata al superadmin per verifica. L'attrezzatura entrerà nel catalogo solo dopo approvazione.
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Nome attrezzatura
              </label>
              <input
                type="text"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
                placeholder="Es. impastatrice a spirale"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Categoria
              </label>
              <input
                type="text"
                value={requestCategory}
                onChange={(e) => setRequestCategory(e.target.value)}
                placeholder="Facoltativa"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Note per la verifica
              </label>
              <textarea
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                rows={4}
                placeholder="Indicazioni utili su uso, reparto o particolarità dell'attrezzatura"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
            </div>

            {requestSuccess ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                {requestSuccess}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                className="h-11 px-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition disabled:opacity-50"
                onClick={() => setRequestOpen(false)}
                disabled={requestSaving}
                type="button"
              >
                Chiudi
              </button>
              <button
                className="h-11 px-4 rounded-2xl bg-[#003B71] text-white font-bold hover:shadow-md transition disabled:opacity-50"
                onClick={submitNewEquipmentRequest}
                disabled={requestSaving}
                type="button"
              >
                {requestSaving ? "Invio..." : "Invia richiesta"}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={modalItem ? `Istruzioni — ${modalItem.nome}` : "Istruzioni"}
        >
          <div className="grid gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2">
                Prodotto consigliato
              </label>
              <select
                value={selectedProductIdx}
                onChange={(e) => {
                  const idx = Number(e.target.value);
                  setSelectedProductIdx(idx);
                  setInstructionForm((prev) => ({
                    ...prev,
                    productName: SANIFICATION_PRODUCTS[idx]?.label || "",
                  }));
                }}
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              >
                {SANIFICATION_PRODUCTS.map((p, idx) => (
                  <option key={idx} value={idx}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <InfoTable product={SANIFICATION_PRODUCTS[selectedProductIdx]} />

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2">
                Prodotto effettivamente utilizzato
              </label>
              <input
                type="text"
                value={instructionForm.productName}
                onChange={(e) =>
                  setInstructionForm((prev) => ({
                    ...prev,
                    productName: e.target.value,
                  }))
                }
                placeholder="Inserisci il nome del prodotto usato"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2">
                Istruzioni operative
              </label>
              <textarea
                rows={4}
                value={instructionForm.usageInstructions}
                onChange={(e) =>
                  setInstructionForm((prev) => ({
                    ...prev,
                    usageInstructions: e.target.value,
                  }))
                }
                placeholder="Es. diluire il prodotto, applicare sulla superficie, lasciare agire, risciacquare e asciugare."
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71] resize-y"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2">
                Note di sicurezza
              </label>
              <textarea
                rows={3}
                value={instructionForm.safetyNotes}
                onChange={(e) =>
                  setInstructionForm((prev) => ({
                    ...prev,
                    safetyNotes: e.target.value,
                  }))
                }
                placeholder="Es. usare guanti, non miscelare con altri prodotti, risciacquare accuratamente."
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71] resize-y"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2">
                Foto attrezzatura
              </label>
              <label className="w-full min-h-12 px-4 py-3 rounded-2xl border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition flex items-center justify-center gap-2 cursor-pointer font-semibold text-gray-700">
                {uploadingReferencePhoto[modalItem?.id] ? (
                  <Upload className="w-4 h-4 animate-pulse" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
                <span>
                  {uploadingReferencePhoto[modalItem?.id]
                    ? "Caricamento foto..."
                    : selectedMeta[modalItem?.id]?.referencePhotoUrl
                      ? "Sostituisci foto"
                      : "Carica foto"}
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  disabled={!!uploadingReferencePhoto[modalItem?.id]}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (modalItem?.id) uploadReferencePhoto(modalItem.id, file);
                  }}
                />
              </label>
              <div className="text-xs text-gray-500 mt-2 mb-3">
                Formati supportati: JPG, PNG e WebP. Dimensione massima 8 MB.
              </div>
              <label className="block text-xs font-semibold text-gray-500 mb-2">
                Oppure URL immagine
              </label>
              <input
                type="text"
                value={instructionForm.imageUrl}
                onChange={(e) =>
                  setInstructionForm((prev) => ({
                    ...prev,
                    imageUrl: e.target.value,
                  }))
                }
                placeholder="https://... oppure lascia vuoto per usare l’icona automatica"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
              <div className="text-xs text-gray-500 mt-2">
                Puoi usare un link in alternativa al caricamento della foto.
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="h-11 px-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition"
                onClick={() => setModalOpen(false)}
                type="button"
                disabled={savingInstructions}
              >
                Chiudi
              </button>
              <button
                className="h-11 px-5 rounded-2xl bg-[#003B71] text-white font-bold hover:shadow-md transition disabled:opacity-50"
                onClick={saveInstructions}
                type="button"
                disabled={savingInstructions}
              >
                {savingInstructions ? "Salvataggio..." : "Salva istruzioni"}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={justifyOpen}
          onClose={() => setJustifyOpen(false)}
          title={justifyItem ? `Giustifica mancata sanificazione — ${justifyItem.nome}` : "Giustifica"}
        >
          <div className="grid gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              Usa questa funzione quando una sanificazione non può essere eseguita per una motivazione reale e documentabile.
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Motivo
              </label>
              <select
                value={justifyReason}
                onChange={(e) => setJustifyReason(e.target.value)}
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              >
                {JUSTIFICATION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <button
                type="button"
                className="rounded-2xl border border-gray-200 bg-white p-3 text-sm hover:bg-gray-50 flex items-center gap-2"
                onClick={() => setJustifyReason("Area in manutenzione")}
              >
                <Hammer className="w-4 h-4 text-slate-600" />
                Manutenzione
              </button>

              <button
                type="button"
                className="rounded-2xl border border-gray-200 bg-white p-3 text-sm hover:bg-gray-50 flex items-center gap-2"
                onClick={() => setJustifyReason("Area inaccessibile")}
              >
                <Ban className="w-4 h-4 text-slate-600" />
                Inaccessibile
              </button>

              <button
                type="button"
                className="rounded-2xl border border-gray-200 bg-white p-3 text-sm hover:bg-gray-50 flex items-center gap-2"
                onClick={() => setJustifyReason("Operatore non in sede")}
              >
                <UserX className="w-4 h-4 text-slate-600" />
                Operatore assente
              </button>

              <button
                type="button"
                className="rounded-2xl border border-gray-200 bg-white p-3 text-sm hover:bg-gray-50 flex items-center gap-2"
                onClick={() => setJustifyReason("Chiusura ristorante")}
              >
                <Store className="w-4 h-4 text-slate-600" />
                Chiusura
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Note
              </label>
              <textarea
                rows={4}
                value={justifyNote}
                onChange={(e) => setJustifyNote(e.target.value)}
                placeholder="Inserisci eventuali dettagli aggiuntivi"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="h-11 px-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition"
                onClick={() => setJustifyOpen(false)}
                type="button"
              >
                Annulla
              </button>
              <button
                className="h-11 px-4 rounded-2xl bg-[#003B71] text-white font-bold hover:shadow-md transition"
                onClick={saveJustification}
                type="button"
              >
                Salva giustifica
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </Page>
  );
}

